import { useToast } from '@ais-ui/ui-core';
import React, { useCallback, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEditEventdetector, useShapeFetch } from '@ais-ui/api';
import { useGetFilters, useGetShareGroups } from '@ais-ui/websockets';
import type { EventdetectorDefinition } from '@ais-ui/api-spec/eventdetector';
import { useLocalization } from '@ais-ui/locale-management';
import {
  areaDetectorFormSchema,
  AreaDetectorFormValues,
  convertFormValuesToBackend,
} from './areaForm';
import { grabEventDetectorInitialGroups } from '../../../Groups/grabInitialGroups';
import { mutateCallbacks } from '../../../utils/mutateCallbacks';
import { AreaDetectorDialog } from './AreaDetectorDialog';
import {
  ActivityFilter,
  reverseConvertActivityFilters,
  useActivityFilters,
} from '../detectorUtils';

interface EditDetectorDialogProps {
  detector: EventdetectorDefinition;
  showDialog: boolean;
  setShowDialog: (showDialog: boolean) => void;
}

export const EditAreaDetectorDialog = ({
  detector,
  showDialog,
  setShowDialog,
}: EditDetectorDialogProps) => {
  const { messages } = useLocalization();
  const { mutate } = useEditEventdetector();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formInitialized, setFormInitialized] = React.useState(false);
  const { toast } = useToast();
  const shapeFetch = useShapeFetch();

  const { data: filters } = useGetFilters();
  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();

  const activityFilters = useActivityFilters();

  const form = useForm<AreaDetectorFormValues>({
    resolver: zodResolver(areaDetectorFormSchema),
    defaultValues: {
      name: detector.name,
      enabled: detector.enabled,
      shapeId: detector.shape,
      selectedGroups: grabEventDetectorInitialGroups(detector, groups),
      filterId: detector.filterId ? detector.filterId : 'no-filter',
      activationText: detector.activationText,
      deactivationText: detector.deactivationText,
      directionMinAngle: detector.checkDirection?.minAngle
        ? detector.checkDirection.minAngle
        : undefined,
      directionMaxAngle: detector.checkDirection?.maxAngle
        ? detector.checkDirection.maxAngle
        : undefined,
      timeBeforeActivation: detector.timeBeforeActivation
        ? detector.timeBeforeActivation
        : undefined,
      timeBeforeDeactivation: detector.timeBeforeDeactivation
        ? detector.timeBeforeDeactivation
        : undefined,
      timeBetweenTrigger: detector.timeBetweenTriggers
        ? detector.timeBetweenTriggers
        : undefined,
      timeoutWhenActive: detector.timeoutWhenActive
        ? detector.timeoutWhenActive
        : undefined,
      severity: detector.severity,
      sendShipNotification: detector.sendShipNotification,
      shipNotificationText: detector.shipNotificationText,
      acknowledgeRequired: detector.ackRequired,
      maintainShiplist: detector.shiplist?.active
        ? detector.shiplist.active
        : false,
      activityFilters: reverseConvertActivityFilters(
        detector.activityFilter as ActivityFilter,
        activityFilters
      ),
      transponderOffDuration: (detector?.activityFilter as any)?.transponder_off
        ? (detector.activityFilter as any).transponder_off
        : undefined,
      navigationStatusTriggerThreshold: detector.navStatusTriggerThreshold,
      navigationStatusIgnoreThreshold: detector.navStatusIgnoreThreshold,
      driftingSpeedOverGroundThreshold: detector.driftingSogThreshold,
      driftingAngleThreshold: detector.driftingChdThreshold,
      driftingTimeThreshold: detector.driftingTimeThreshold,
      exitCheckThreshold: detector.exitCheckThreshold,
      exitCheckTriggerLost: detector.exitCheckTriggerLost,
      deltaSpeedOverGroundThreshold: detector.deltaSogThreshold,
      useVesselDimensions: detector.useVesselDimensions,
      entireVesselInside: detector.entireVessel,
      emails: detector.notify
        ? detector.notify
            .filter((notifier) => notifier.action === 'email')
            .map((notifier) => notifier.who)
        : [],
      phones: detector.notify
        ? detector.notify
            .filter((notifier) => notifier.action === 'sms')
            .map((notifier) => notifier.who)
        : [],
      circleCenterCpa: detector.circleCenterCpa,
      checkCpa: detector.checkCpa,
      cpaLimit: detector.cpaLimit,
      tcpaLimit: detector.tcpaLimit,
    },
  });

  useEffect(() => {
    if (!isLoadingGroups && !formInitialized) {
      form.reset({
        ...form.getValues(), // Preserve other values
        selectedGroups: grabEventDetectorInitialGroups(detector, groups),
      });
      setFormInitialized(true); // Prevent re-initialization
    }
  }, [isLoadingGroups, groups, form, formInitialized, detector]);

  const onEdit = useCallback(
    async (data: AreaDetectorFormValues) => {
      if (!filters) {
        return;
      }

      let shape = null;
      if (data.shapeId) {
        shape = await shapeFetch(data.shapeId);
      }

      if (!shape) {
        return;
      }

      const filter = filters.find((filter) => filter.uuid === data.filterId);

      const backendValues = convertFormValuesToBackend(
        detector.type,
        data,
        shape,
        filter
      );

      setIsSubmitting(true);

      const callBacks = mutateCallbacks(
        toast,
        messages,
        'edit',
        'detector',
        setIsSubmitting,
        setShowDialog
      );

      mutate(
        {
          id: detector.id as string,
          eventdetectorDefinition: backendValues,
        },
        callBacks
      );
    },
    [filters, shapeFetch, mutate, detector.type, detector.id]
  );

  return (
    <AreaDetectorDialog
      form={form}
      onSubmit={onEdit}
      setShowOuterDialog={setShowDialog}
      mutateType="edit"
      isSubmitting={isSubmitting}
    ></AreaDetectorDialog>
  );
};
